<template>
  <div class="v-form-question-repeater">
    <VCard v-for="(repeatable, index) in repeatableQuestions" :key="index" no-border :title="`${repeatable.repeatable_item_name} ${index + 1}`" type="page-background">
      <template v-if="index > 0" #header-right>
        <VButton :label="i18n.remove" size="small" type="secondary" @click="remove(repeatable)" />
      </template>

      <VFormBlueprintQuestion
        v-for="childQuestion in blueprint.questions.filter((child) => child.parent_question_id === repeatable.id)"
        :key="childQuestion.id"
        :blueprint
        :question="childQuestion"
        @updated="onQuestionUpdated($event)"
      >
        <template #answer="slotProps">
          <slot name="answer" v-bind="slotProps" />
        </template>
        <template #after-question-answer="slotProps">
          <slot name="after-question-answer" v-bind="slotProps" />
        </template>
        <template #file-name="slotProps">
          <slot name="file-name" v-bind="slotProps" />
        </template>
      </VFormBlueprintQuestion>
    </VCard>

    <VButton icon="Plus" :label="i18nFormBuilder.addAnotherRepeatableItem.replace('$repeatable', question.repeatable_item_name)" @click="add" />
  </div>
</template>

<script setup lang="ts" generic="TBlueprint extends Blueprint = Blueprint">
import { computed } from 'vue'

import { type FileLike } from '../types.ts'
import { i18nForm as i18n, i18nFormBuilder } from '../utils/i18n.ts'
import { type Blueprint, type Question, type RepeaterQuestion } from './FormBuilder/useBlueprint.ts'
import VButton from './VButton.vue'
import VCard from './VCard.vue'
import VFormBlueprintQuestion from './VFormBlueprintQuestion.vue'

interface Props {
  blueprint: TBlueprint
  question: RepeaterQuestion
}

const { blueprint, question } = defineProps<Props>()

defineSlots<{
  answer(props: { question: Question }): any
  'after-question-answer'(props: { question: Question }): any
  'file-name'(props: { file: FileLike; index: number; question: Question }): any
}>()

const emit = defineEmits<{
  updated: [question: Question]
}>()

const repeatableQuestions = computed(
  (): RepeaterQuestion[] => blueprint.questions.filter((q) => q.type === 'repeater' && !q.is_template && q.parent_question_id === question.id) as RepeaterQuestion[],
)

function onQuestionUpdated(question: Question) {
  delete blueprint.errors[question.id]
  emit('updated', question)
}

async function add() {
  await blueprint.copyQuestion({
    question,
    properties: { parent_question_id: question.id, is_template: false },
    omitProperties: ['is_template'],
    filter: (question) => question.is_template !== false,
  })
}

async function remove(question: RepeaterQuestion) {
  await blueprint.removeQuestion(question)
}
</script>

<style>
.v-form-question-repeater {
  width: 100%;
  position: relative;
}

.v-form-question--repeater {
  /* Only apply this to the main of the entity question, not to nested questions. */
  .v-form-question-main:not(.v-form-question-main .v-form-question-main) {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
  }

  &.is-internal .v-card--page-background {
    background-color: #fff;
    border-color: #fff;
  }
}
</style>
